import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import { Gauge, Save } from 'lucide-react';
import {
  AnonymousLimitConfig,
  useAnonymousLimits,
  useUpdateAnonymousLimits,
} from '../../services/anonymousLimits.service';
import { EmptyState } from '../../components/admin/EmptyState';
import { useNotification } from '../../hooks/useNotification';
import { FormattedNumberTextField } from '../../components/common/FormattedNumberTextField';
import { formatAdminNumber } from '../../utils/numberFormat';

const fieldLabels: Record<keyof AnonymousLimitConfig, { label: string; helperText: string }> = {
  maxMessagesPerSession: {
    label: 'Messages per conversation',
    helperText: 'Maximum user messages allowed in one anonymous chat session.',
  },
  maxChatSessionsPerDay: {
    label: 'Chat sessions per day',
    helperText: 'Reloading and starting again consumes this daily quota.',
  },
  maxFilesPerDay: {
    label: 'Files per day',
    helperText: 'Maximum number of non-image files an anonymous user can upload per day.',
  },
  maxImagesPerDay: {
    label: 'Images per day',
    helperText: 'Maximum number of images an anonymous user can upload per day.',
  },
  maxFileBytesPerDay: {
    label: 'Upload bytes per day',
    helperText: 'Total uploaded file and image size allowed per day.',
  },
  maxCreditsPerDay: {
    label: 'Credits per day',
    helperText: 'Daily spend cap per anonymous user, in credits (same unit as quota; 1 USD = billing.credits_per_usd).',
  },
  maxRequestsPerMinute: {
    label: 'Messages per minute',
    helperText: 'Per-user rate limit for anonymous chat messages.',
  },
};

const fields = Object.keys(fieldLabels) as (keyof AnonymousLimitConfig)[];

export const AnonymousLimits: React.FC = () => {
  const notification = useNotification();
  const { data, isLoading, error } = useAnonymousLimits();
  const updateMutation = useUpdateAnonymousLimits();
  const [config, setConfig] = useState<AnonymousLimitConfig | null>(null);

  useEffect(() => {
    if (data?.config) {
      setConfig(data.config);
    }
  }, [data]);

  const hasChanges = useMemo(() => {
    if (!config || !data?.config) return false;

    return fields.some((field) => config[field] !== data.config[field]);
  }, [config, data]);

  const tokenLimitInvalid =
    config !== null &&
    data?.freeRegisteredTokenLimit !== null &&
    data?.freeRegisteredTokenLimit !== undefined &&
    config.maxCreditsPerDay >= data.freeRegisteredTokenLimit;

  const handleNumberChange = (field: keyof AnonymousLimitConfig, value: number | undefined) => {
    setConfig((previous) => {
      if (!previous) return previous;

      return {
        ...previous,
        [field]: value !== undefined && Number.isFinite(value) && value >= 0 ? value : 0,
      };
    });
  };

  const handleSave = async () => {
    if (!config) return;

    try {
      await updateMutation.mutateAsync(config);
      notification.success('Anonymous limits updated successfully');
    } catch (saveError) {
      const message =
        saveError instanceof Error ? saveError.message : 'Failed to update anonymous limits';
      notification.error(message);
    }
  };

  return (
    <Box className="h-full bg-slate-50 p-8 dark:bg-slate-900">
      <Box className="mb-6 flex items-start justify-between gap-4">
        <Box>
          <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
            Anonymous Limits
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Configure guest chat, upload, rate, and credit limits.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={
            updateMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <Save size={20} />
          }
          onClick={handleSave}
          disabled={!hasChanges || updateMutation.isPending || tokenLimitInvalid}
          sx={{
            backgroundColor: '#3B82F6',
            '&:hover': {
              backgroundColor: '#2563EB',
            },
            '&:disabled': {
              backgroundColor: '#94a3b8',
            },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {isLoading ? (
        <Box className="flex h-[400px] items-center justify-center">
          <CircularProgress size={40} className="text-blue-500" />
        </Box>
      ) : error || !config ? (
        <EmptyState
          Icon={Gauge}
          title="Failed to load anonymous limits"
          description="Please try again later"
        />
      ) : (
        <Paper
          elevation={0}
          className="rounded-xl bg-white p-6 dark:bg-slate-800"
          sx={{
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
          }}
        >
          <Box className="mb-5">
            <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
              Guest account quota
            </Typography>
            <Typography variant="body2" className="mt-1 text-gray-600 dark:text-slate-400">
              Anonymous users can only create chat sessions and message the system default agent.
            </Typography>
          </Box>

          {data.freeRegisteredTokenLimit !== null && (
            <Alert severity={tokenLimitInvalid ? 'error' : 'info'} className="mb-5">
              Free registered quota: {formatAdminNumber(data.freeRegisteredTokenLimit)} credits.
              Anonymous credits per day must stay lower than the FREE package quota.
            </Alert>
          )}

          <Box className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {fields.map((field) => (
              <FormattedNumberTextField
                key={field}
                label={fieldLabels[field].label}
                helperText={fieldLabels[field].helperText}
                value={config[field]}
                onValueChange={(value) => handleNumberChange(field, value)}
                error={field === 'maxCreditsPerDay' && tokenLimitInvalid}
                fullWidth
                inputProps={{ min: 0, step: 1 }}
              />
            ))}
          </Box>

          <Divider className="my-6" />

          <Box className="grid grid-cols-1 gap-3 text-sm text-gray-700 dark:text-slate-300 md:grid-cols-3">
            <Box>Quota scope: anonymous userId</Box>
            <Box>LLM quota check: before model call</Box>
            <Box>Limit response: sign up or upgrade CTA</Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};
